import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { AgGridReact } from 'ag-grid-react';
import type { RootState } from '@/redux-toolkit/store';
import {
  AllCommunityModule,
  ModuleRegistry,
  type BodyScrollEvent,
  type GridApi,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import './shipments.css';
import type { ShipmentRow } from '../../types';
import type {
  ShipmentTableActionKey,
  ShipmentVirtualTableProps,
} from './ShipmentVirtualTable.types';
import {
  CORE_COLUMN_KEYS,
  DEFAULT_PAGE_SIZE,
  DEFAULT_ROW_HEIGHT,
  GRID_LOAD_MORE_OFFSET_PX,
  INITIAL_SKELETON_ROW_COUNT,
  PLACEHOLDER_ROW_KEY,
  SKELETON_HEADERS,
  STICKY_COLUMN_MIN_WIDTH,
} from './shipmentTableConstants';
import { getShipmentRowId, isPlaceholderRow } from './shipmentTableUtils';
import {
  createElSubRows,
  getElSubRowParentId,
  getElSubRowParentRef,
  getElSubRowPosition,
  isAccordionToggleTarget,
  isElSubRow,
  shouldShowExecutionLegAccordion,
} from './shipmentAccordionUtils';
import { useShipmentTableColumns } from './useShipmentTableColumns';
import { useShipmentGridScrollRestore } from './useShipmentGridScrollRestore';

ModuleRegistry.registerModules([AllCommunityModule]);

export type { ShipmentTableActionKey };

const ShipmentVirtualTable: React.FC<ShipmentVirtualTableProps> = ({
  headerList,
  shipmentData,
  activeLegType,
  customerLegEnabled = false,
  loading,
  loadingMore,
  hasMore,
  pageSize: _pageSize = DEFAULT_PAGE_SIZE,
  checked,
  selectedIds,
  excludedIds,
  isSelectAllMode,
  openShipmentId,
  highlightedShipmentId,
  onSelectChange,
  onOpenShipment,
  onOpenExecutionLeg,
  onShipmentAction,
  onLoadMore,
  allPermissions,
}) => {
  const darkModeEnabled = useSelector(
    (state: RootState) => state.appearance.darkModeEnabled
  );
  const tableHostRef = useRef<HTMLDivElement | null>(null);
  const gridApiRef = useRef<GridApi<ShipmentRow> | null>(null);
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const [tableWidth, setTableWidth] = useState(0);
  const [stickyColumnsEnabled, setStickyColumnsEnabled] = useState(
    () => window.innerWidth >= STICKY_COLUMN_MIN_WIDTH
  );
  const [shouldUseFullWidth, setShouldUseFullWidth] = useState(() => {
    const enabledColumnKeys = headerList
      .filter((header) => header.enable !== false)
      .map((header) => header.key);

    const hasOnlyCore =
      enabledColumnKeys.every((key) => CORE_COLUMN_KEYS.has(key)) &&
      enabledColumnKeys.length > 0;

    const screenWidth = window.innerWidth;

    // On 1280px+ screens, ensure service level is visible without scroll
    if (screenWidth >= 1280) {
      return true; // Use full width on 1280px+ to ensure service level fits
    }

    // Below 1280px, use full width only if core columns only
    return hasOnlyCore;
  });
  const [expandedShipmentIds, setExpandedShipmentIds] = useState<
    Record<string, boolean>
  >({});
  const hasMoreRef = useRef(hasMore);
  const loadingRef = useRef(loading || loadingMore);
  const onLoadMoreRef = useRef(onLoadMore);
  const showFullTableSkeleton = Boolean(loading);
  const hasPlaceholderRows = shipmentData.some((row) => isPlaceholderRow(row));
  const effectiveHeaderList =
    (showFullTableSkeleton || hasPlaceholderRows) && headerList.length === 0
      ? SKELETON_HEADERS
      : headerList;
  const hasAdditionalColumns = useMemo(() => {
    const enabledColumnKeys = effectiveHeaderList
      .filter((header) => header.enable !== false)
      .map((header) => header.key);

    return enabledColumnKeys.some((key) => !CORE_COLUMN_KEYS.has(key));
  }, [effectiveHeaderList]);

  const gridRows = useMemo(() => {
    if (showFullTableSkeleton) {
      return Array.from(
        { length: INITIAL_SKELETON_ROW_COUNT },
        (_, index) =>
          ({
            [PLACEHOLDER_ROW_KEY]: `shipment-initial-skeleton-${index}`,
          }) as ShipmentRow
      );
    }

    // Inject EL sub-rows as full grid rows immediately after their parent CL.
    const result: ShipmentRow[] = [];
    for (const row of shipmentData) {
      result.push(row);
      const rowId = getShipmentRowId(row);
      if (
        expandedShipmentIds[rowId] &&
        shouldShowExecutionLegAccordion(activeLegType, row, customerLegEnabled)
      ) {
        result.push(...createElSubRows(row, rowId));
      }
    }
    return result;
  }, [
    shipmentData,
    showFullTableSkeleton,
    expandedShipmentIds,
    activeLegType,
    customerLegEnabled,
  ]);

  const hasRenderableRows = gridRows.some((row) => !isPlaceholderRow(row));
  const shouldHideActionColumn = !hasRenderableRows;

  useLayoutEffect(() => {
    hasMoreRef.current = hasMore;
    loadingRef.current = loading || loadingMore;
    onLoadMoreRef.current = onLoadMore;
  }, [hasMore, loading, loadingMore, onLoadMore]);

  useLayoutEffect(() => {
    const updateViewportWidth = () => {
      setViewportWidth(window.innerWidth);
    };

    updateViewportWidth();
    window.addEventListener('resize', updateViewportWidth);

    return () => {
      window.removeEventListener('resize', updateViewportWidth);
    };
  }, []);

  useLayoutEffect(() => {
    const gridElement = tableHostRef.current;
    if (!gridElement) return undefined;

    const updateTableWidth = () => {
      const gridRect = gridElement.getBoundingClientRect();
      const openDrawer = document.querySelector<HTMLElement>(
        '.custom-card-drawer.open'
      );
      const drawerWidth = openDrawer?.getBoundingClientRect().width;
      const drawerLeft =
        typeof drawerWidth === 'number'
          ? window.innerWidth - drawerWidth
          : undefined;
      const visibleWidth =
        typeof drawerLeft === 'number'
          ? Math.min(gridRect.width, Math.max(0, drawerLeft - gridRect.left))
          : gridRect.width;

      setTableWidth(Math.max(0, Math.floor(visibleWidth)));
    };

    updateTableWidth();
    const animationFrame = window.requestAnimationFrame(updateTableWidth);
    const transitionTimer = window.setTimeout(updateTableWidth, 350);

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateTableWidth);
      return () => {
        window.cancelAnimationFrame(animationFrame);
        window.clearTimeout(transitionTimer);
        window.removeEventListener('resize', updateTableWidth);
      };
    }

    const resizeObserver = new ResizeObserver(updateTableWidth);
    resizeObserver.observe(gridElement);
    document
      .querySelectorAll<HTMLElement>('.custom-card-drawer')
      .forEach((drawerElement) => {
        resizeObserver.observe(drawerElement);
      });

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.clearTimeout(transitionTimer);
      resizeObserver.disconnect();
    };
  }, [openShipmentId]);

  useLayoutEffect(() => {
    const updateStickyColumns = () => {
      setStickyColumnsEnabled(window.innerWidth >= STICKY_COLUMN_MIN_WIDTH);
    };

    const updateWidthBehavior = () => {
      const enabledColumnKeys = effectiveHeaderList
        .filter((header) => header.enable !== false)
        .map((header) => header.key);

      const hasOnlyCore =
        enabledColumnKeys.every((key) => CORE_COLUMN_KEYS.has(key)) &&
        enabledColumnKeys.length > 0;

      const screenWidth = window.innerWidth;

      // On 1280px+ screens, ensure service level is visible without scroll
      if (screenWidth >= 1280) {
        setShouldUseFullWidth(true); // Use full width on 1280px+ to ensure service level fits
      } else {
        // Below 1280px, use full width only if core columns only
        setShouldUseFullWidth(hasOnlyCore);
      }
    };

    updateStickyColumns();
    updateWidthBehavior();
    window.addEventListener('resize', updateStickyColumns);
    window.addEventListener('resize', updateWidthBehavior);

    return () => {
      window.removeEventListener('resize', updateStickyColumns);
      window.removeEventListener('resize', updateWidthBehavior);
    };
  }, [effectiveHeaderList]);

  useLayoutEffect(() => {
    setExpandedShipmentIds((previous) => {
      if (!Object.keys(previous).length) return previous;
      const availableIds = new Set(
        shipmentData
          .filter((row) =>
            shouldShowExecutionLegAccordion(
              activeLegType,
              row,
              customerLegEnabled
            )
          )
          .map((row) => getShipmentRowId(row))
      );
      const nextEntries = Object.entries(previous).filter(
        ([shipmentId, isExpanded]) => isExpanded && availableIds.has(shipmentId)
      );
      const next = Object.fromEntries(nextEntries);
      return Object.keys(next).length === Object.keys(previous).length
        ? previous
        : next;
    });
  }, [activeLegType, customerLegEnabled, shipmentData]);

  useLayoutEffect(() => {
    gridApiRef.current?.resetRowHeights();
  }, [expandedShipmentIds]);

  const getRowBackground = useCallback(
    (record: ShipmentRow) => {
      const rowId = getShipmentRowId(record);
      return openShipmentId === rowId || highlightedShipmentId === rowId
        ? 'var(--surface-selected)'
        : undefined;
    },
    [highlightedShipmentId, openShipmentId]
  );

  const drawerFourColumnMode =
    Boolean(openShipmentId) && viewportWidth >= 1000 && viewportWidth <= 2000;
  const estimatedDrawerWidth = viewportWidth <= 1240 ? 470 : 552;
  const drawerGridWidth =
    drawerFourColumnMode && tableWidth > 0
      ? tableWidth
      : drawerFourColumnMode
        ? Math.max(0, viewportWidth - estimatedDrawerWidth)
        : 0;
  const gridLayoutKey = drawerFourColumnMode
    ? 'shipment-grid-drawer-layout'
    : 'shipment-grid-full-layout';

  const {
    captureVerticalScrollTop,
    handleGridReady,
    initialScrollState,
    rememberVerticalScrollTop,
  } = useShipmentGridScrollRestore({
    gridApiRef,
    gridLayoutKey,
    rowHeight: DEFAULT_ROW_HEIGHT,
    tableHostRef,
  });

  const columns = useShipmentTableColumns({
    checked,
    effectiveHeaderList,
    excludedIds,
    getRowBackground,
    isSelectAllMode,
    onSelectChange,
    onShipmentAction,
    selectedIds,
    shouldHideActionColumn,
    stickyColumnsEnabled,
    allPermissions,
    shouldUseFullWidth,
    drawerFourColumnMode,
    drawerWideTrackingColumn: drawerFourColumnMode && viewportWidth <= 1500,
    activeLegType,
    customerLegEnabled,
    isAccordionExpanded: (shipmentId) =>
      Boolean(expandedShipmentIds[shipmentId]),
    onAccordionToggle: (shipment) => {
      if (
        !shouldShowExecutionLegAccordion(
          activeLegType,
          shipment,
          customerLegEnabled
        )
      ) {
        return;
      }
      const shipmentId = getShipmentRowId(shipment);
      setExpandedShipmentIds((previous) => ({
        ...previous,
        [shipmentId]: !previous[shipmentId],
      }));
    },
  });

  const handleGridScroll = useCallback(
    (event: BodyScrollEvent<ShipmentRow>) => {
      if (event.direction !== 'vertical') return;
      rememberVerticalScrollTop(event);
      if (!hasMoreRef.current || loadingRef.current) return;

      const verticalRange = event.api.getVerticalPixelRange();
      const rowCount = event.api.getDisplayedRowCount();
      const estimatedBottom = rowCount * DEFAULT_ROW_HEIGHT;

      if (verticalRange.bottom >= estimatedBottom - GRID_LOAD_MORE_OFFSET_PX) {
        onLoadMoreRef.current?.();
      }
    },
    [rememberVerticalScrollTop]
  );

  useLayoutEffect(() => {
    const gridElement = tableHostRef.current;
    if (!gridElement || openShipmentId) return undefined;

    const getScrollElements = () => {
      const centerViewport = gridElement.querySelector<HTMLElement>(
        '.ag-center-cols-viewport'
      );
      const headerViewport = gridElement.querySelector<HTMLElement>(
        '.ag-header-viewport'
      );
      const fakeHorizontalViewport = gridElement.querySelector<HTMLElement>(
        '.ag-body-horizontal-scroll-viewport, .ag-horizontal-scroll-viewport'
      );

      return { centerViewport, fakeHorizontalViewport, headerViewport };
    };

    const syncHorizontalScroll = (scrollLeft: number) => {
      const { centerViewport, fakeHorizontalViewport, headerViewport } =
        getScrollElements();

      if (headerViewport && headerViewport.scrollLeft !== scrollLeft) {
        headerViewport.scrollLeft = scrollLeft;
      }

      if (
        fakeHorizontalViewport &&
        fakeHorizontalViewport.scrollLeft !== scrollLeft
      ) {
        fakeHorizontalViewport.scrollLeft = scrollLeft;
      }

      if (centerViewport && centerViewport.scrollLeft !== scrollLeft) {
        centerViewport.scrollLeft = scrollLeft;
      }
    };

    const handleCenterScroll = () => {
      const { centerViewport } = getScrollElements();
      if (!centerViewport) return;
      syncHorizontalScroll(centerViewport.scrollLeft);
    };

    const handleHorizontalWheel = (event: WheelEvent) => {
      const { centerViewport } = getScrollElements();
      if (!centerViewport) return;

      const horizontalDelta =
        Math.abs(event.deltaX) >= Math.abs(event.deltaY) || event.shiftKey
          ? event.deltaX || event.deltaY
          : 0;

      if (horizontalDelta === 0) return;

      const maxScrollLeft =
        centerViewport.scrollWidth - centerViewport.clientWidth;
      const nextScrollLeft = Math.max(
        0,
        Math.min(maxScrollLeft, centerViewport.scrollLeft + horizontalDelta)
      );

      event.preventDefault();
      syncHorizontalScroll(nextScrollLeft);
    };

    gridElement.addEventListener('wheel', handleHorizontalWheel, {
      capture: true,
      passive: false,
    });
    gridElement
      .querySelector('.ag-center-cols-viewport')
      ?.addEventListener('scroll', handleCenterScroll, { capture: true });

    return () => {
      gridElement.removeEventListener('wheel', handleHorizontalWheel, {
        capture: true,
      });
      gridElement
        .querySelector('.ag-center-cols-viewport')
        ?.removeEventListener('scroll', handleCenterScroll, { capture: true });
    };
  }, [openShipmentId]);

  // Block/unblock horizontal scroll when drawer state changes
  useLayoutEffect(() => {
    const blockHorizontalScroll = (block: boolean) => {
      const gridElement = tableHostRef.current;
      if (!gridElement) return;

      // Target all possible AG-Grid scroll containers
      const scrollContainers = [
        gridElement,
        gridElement.querySelector('.ag-root-wrapper'),
        gridElement.querySelector('.ag-body-viewport'),
        gridElement.querySelector('.ag-center-cols-viewport'),
        gridElement.querySelector('.ag-horizontal-scroll-viewport'),
      ];

      scrollContainers.forEach((container) => {
        if (container) {
          (container as HTMLElement).style.overflowX = block ? 'hidden' : '';
        }
      });
    };

    blockHorizontalScroll(!!openShipmentId);
  }, [openShipmentId]);

  return (
    <div
      ref={tableHostRef}
      className={`shipment-grid ag-theme-quartz${darkModeEnabled ? ' ag-theme-quartz-dark' : ''}`}
      data-drawer-open={openShipmentId ? 'true' : 'false'}
      data-four-column-mode={drawerFourColumnMode ? 'true' : 'false'}
      data-has-additional-columns={hasAdditionalColumns ? 'true' : 'false'}
    >
      <AgGridReact<ShipmentRow>
        key={gridLayoutKey}
        theme='legacy'
        rowData={gridRows}
        columnDefs={columns}
        initialState={initialScrollState}
        loading={false}
        rowHeight={DEFAULT_ROW_HEIGHT}
        onGridReady={handleGridReady}
        headerHeight={28}
        animateRows={false}
        rowBuffer={30}
        suppressAnimationFrame={true}
        suppressCellFocus={true}
        suppressColumnMoveAnimation={true}
        overlayNoRowsTemplate='<span class="shipment-grid-no-data">No data</span>'
        suppressColumnVirtualisation={false}
        suppressRowVirtualisation={false}
        getRowId={(params) => getShipmentRowId(params.data)}
        onBodyScroll={handleGridScroll}
        onRowClicked={(event) => {
          const target =
            event.event instanceof Event
              ? (event.event.target as HTMLElement | null)
              : null;
          if (
            target?.closest(
              '.ant-checkbox-wrapper, .ant-checkbox, .shipment-grid-action'
            )
          ) {
            return;
          }
          if (isAccordionToggleTarget(target)) {
            return;
          }

          const record = event.data;
          if (!record || isPlaceholderRow(record)) return;
          captureVerticalScrollTop();
          if (isElSubRow(record)) {
            onOpenExecutionLeg?.(
              getShipmentRowId(record),
              getElSubRowParentId(record),
              getElSubRowParentRef(record)
            );
            return;
          }
          onOpenShipment(getShipmentRowId(record), record);
        }}
        getRowStyle={(params) => {
          const record = params.data;
          if (!record) return undefined;
          const background = getRowBackground(record);
          return background ? { background } : undefined;
        }}
        getRowClass={(params) => {
          const record = params.data;
          if (!record) return undefined;
          const rowId = getShipmentRowId(record);
          if (expandedShipmentIds[rowId]) {
            return 'shipment-grid-customer-execution-parent-row';
          }
          if (isElSubRow(record)) {
            return `shipment-grid-customer-execution-child-row is-${getElSubRowPosition(
              record
            )}`;
          }
          return undefined;
        }}
        containerStyle={{
          height: '100%',
          width:
            drawerFourColumnMode && drawerGridWidth > 0
              ? drawerGridWidth
              : shouldUseFullWidth
                ? '100%'
                : 'auto',
          minWidth:
            drawerFourColumnMode && drawerGridWidth > 0
              ? drawerGridWidth
              : shouldUseFullWidth
                ? '100%'
                : 'auto',
        }}
      />
    </div>
  );
};

export default ShipmentVirtualTable;
